import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';
import {
    analysisStatus, analyzeProduct, calculateReviews, downloadFeedbackBase, getKnowledge, loadAnalyticsProducts, loadProductReviews,
} from '../../../../services/feedback';
import FeedbackFaq from './FeedbackFaq';
import FeedbackReports from './FeedbackReports';
import { Stars, errorText } from './feedbackUi';

const PERIODS = [[30, '30 дней'], [90, '90 дней'], [180, '180 дней'], [365, 'Год'], [3650, 'Всё время']];
const PAGE = 50;
const analysisPeriod = (period) => {
    if (period?.from && period?.to) return `${period.from.split('-').reverse().join('.')} – ${period.to.split('-').reverse().join('.')}`;
    if (period?.days) return period.days >= 3650 ? 'всё время' : `${period.days} дн.`;
    return 'неизвестный период';
};
const REVIEW_FILTERS = [['all', 'Все с текстом'], ['5', '5★'], ['4', '4★'], ['3', '3★'], ['2', '2★'], ['1', '1★']];

function Distribution({ distribution, total }) {
    return (
        <div className="fb-bars">
            {[5, 4, 3, 2, 1].map((star) => {
                const count = distribution[String(star)] || 0;
                return (
                    <div key={star} className={`fb-bar${star <= 3 ? ' fb-bar--low' : ''}`}>
                        <span style={{ width: 22 }}>{star}★</span>
                        <div className="fb-bar__track"><div className="fb-bar__fill" style={{ width: `${total ? (count / total) * 100 : 0}%` }} /></div>
                        <span style={{ width: 26, textAlign: 'right' }}>{count}</span>
                    </div>
                );
            })}
        </div>
    );
}

function Calculator({ accountId, rating, count }) {
    const { request } = useApi();
    const [form, setForm] = useState({ rating: rating || 4.5, count: count || 100, target: 4.8, star: 5 });
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setForm((current) => ({ ...current, rating: rating || current.rating, count: count || current.count }));
    }, [rating, count]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            calculateReviews({ request, accountId, ...form })
                .then((data) => { if (!cancelled) { setResult(data); setError(''); } })
                .catch((err) => { if (!cancelled) setError(errorText(err)); });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [request, accountId, form]);

    const field = (key, label, props = {}) => (
        <label>
            {label}
            <input className="fb-input" type="number" value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} {...props} />
        </label>
    );

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h4 style={{ margin: 0 }}>Калькулятор отзывов</h4>
            <div className="fb-muted">Сколько отзывов с нужной оценкой надо получить, чтобы рейтинг на Ozon вырос. Ozon показывает рейтинг с одним знаком, поэтому 4.8 значит от 4.75.</div>
            <div className="fb-calc">
                {field('rating', 'Рейтинг сейчас', { step: 0.1, min: 1, max: 5 })}
                {field('count', 'Отзывов сейчас', { min: 0 })}
                {field('target', 'Нужный рейтинг', { step: 0.1, min: 1, max: 5 })}
                <label>
                    Оценка новых отзывов
                    <select className="fb-input" value={form.star} onChange={(event) => setForm({ ...form, star: event.target.value })}>
                        {[5, 4, 3].map((star) => <option key={star} value={star}>{star}★</option>)}
                    </select>
                </label>
            </div>
            {error && <div className="fb-note">{error}</div>}
            {result && (
                <div>
                    <div className="fb-result">{result.reachable ? (result.needed === 0 ? 'Цель уже достигнута' : `Нужно ${result.needed} отзывов`) : 'Недостижимо'}</div>
                    <div className="fb-note">{result.message}{result.final_rating ? `. Итоговый рейтинг ≈ ${result.final_rating}` : ''}</div>
                </div>
            )}
        </div>
    );
}

function ProductDetail({ accountId, product, period, onClose }) {
    const { request } = useApi();
    const [reviews, setReviews] = useState(null);
    const [star, setStar] = useState('all');
    const [counts, setCounts] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [runKey, setRunKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setReviews(null);
        setMessage('');
        loadProductReviews({ request, accountId, sku: product.sku, period, rating: star === 'all' ? undefined : Number(star) })
            .then((data) => { if (!cancelled) { setReviews(data.items); setCounts(data.counts || null); } })
            .catch((error) => !cancelled && setMessage(errorText(error)));
        return () => { cancelled = true; };
    }, [request, accountId, product.sku, period, star]);

    useEffect(() => {
        let cancelled = false;
        setAnalysis(null);
        getKnowledge({ request, accountId, sku: product.sku })
            .then((data) => !cancelled && setAnalysis(data.analysis ? { ...data.analysis, at: data.analysis_at } : null))
            .catch(() => {});
        return () => { cancelled = true; };
    }, [request, accountId, product.sku]);

    // Анализ идёт на сервере в фоне: страницу можно покинуть, при возвращении состояние подхватывается.
    const loadSaved = useCallback(async () => {
        const data = await getKnowledge({ request, accountId, sku: product.sku });
        setAnalysis(data.analysis ? { ...data.analysis, at: data.analysis_at } : null);
    }, [request, accountId, product.sku]);

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const tick = async () => {
            try {
                const status = await analysisStatus({ request, accountId, sku: product.sku });
                if (cancelled) return;
                if (status.state === 'running') {
                    setBusy(true);
                    timer = setTimeout(tick, 3000);
                    return;
                }
                setBusy(false);
                if (status.state === 'done') await loadSaved();
                if (status.state === 'error') setMessage(errorText({ message: JSON.stringify({ code: status.error }) }));
            } catch (error) {
                if (!cancelled) setBusy(false);
            }
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [request, accountId, product.sku, loadSaved, runKey]);

    const analyze = async () => {
        setBusy(true);
        setMessage('');
        try {
            await analyzeProduct({ request, accountId, sku: product.sku, period });
            setRunKey((value) => value + 1);
        } catch (error) {
            setMessage(errorText(error));
            setBusy(false);
        }
    };

    const list = (title, items) => (items && items.length > 0 ? (
        <div>
            <b>{title}</b>
            <ul className="fb-points">{items.map((item, index) => <li key={index}>{item.text}{item.count ? ` · ${item.count}` : ''}</li>)}</ul>
        </div>
    ) : null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>{product.name || 'Товар'}</h4>
                        <div className="fb-muted">SKU {product.sku}{product.article ? ` · ${product.article}` : ''} · отзывов {product.reviews}, рейтинг {product.average_rating ?? '—'}</div>
                    </div>
                    <button type="button" onClick={onClose}>Закрыть</button>
                </div>
                <div className="fb-actions">
                    <button type="button" className="primary" disabled={busy || !product.ready} onClick={analyze}
                        title={product.ready ? (analysis ? 'Пересчитать разбор по свежим отзывам за выбранный период. Услуга платная, прежний разбор заменится' : 'ИИ разберёт тексты отзывов: что нравится, что нет и почему. Услуга платная') : 'Для анализа нужно хотя бы 3 отзыва с текстом за период'}>
                        {analysis ? 'Актуализировать анализ' : 'Проанализировать причины (ИИ)'}
                    </button>
                    {!product.ready && <span className="fb-note">Отзывов с текстом: {product.with_text}. Для анализа нужно 3 и больше.</span>}
                    {message && <span className="fb-note">{message}</span>}
                </div>
                {analysis && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {analysis.summary && <div>{analysis.summary}</div>}
                        <div className="fb-two">
                            <div>{list('Что нравится', analysis.advantages)}{list('Что не нравится', analysis.disadvantages)}</div>
                            <div>{list('Вероятные причины', analysis.causes)}
                                {analysis.recommendations && analysis.recommendations.length > 0 && (
                                    <div><b>Что сделать магазину</b><ul className="fb-points">{analysis.recommendations.map((item, index) => <li key={index}>{item}</li>)}</ul></div>
                                )}
                            </div>
                        </div>
                        <div className="fb-note">По {analysis.reviews_analyzed || '—'} отзывам с текстом за {analysisPeriod(analysis.period)}{analysis.at ? `, сделан ${fmtDateTime(analysis.at)}` : ''}</div>
                    </div>
                )}
            </div>
            <div className="fb-card">
                <div className="account-tabs account-tabs--secondary">
                    {REVIEW_FILTERS.map(([value, label]) => (
                        <button key={value} type="button" className={star === value ? 'active' : ''} onClick={() => setStar(value)}>
                            {label}{counts ? ` · ${counts[value] ?? 0}` : ''}
                        </button>
                    ))}
                </div>
                {reviews && reviews.length === 0 && <div className="fb-muted" style={{ marginTop: 8 }}>За выбранный период таких отзывов с текстом нет.</div>}
                <div className="fb-list" style={{ marginTop: 8 }}>
                    {(reviews || []).map((review) => (
                        <div key={review.id} className="fb-item" style={{ padding: '10px 14px' }}>
                            <div className="fb-item__head">
                                <Stars rating={review.rating} />
                                <span className="fb-item__meta">{review.published_at ? fmtDateTime(review.published_at) : ''}</span>
                            </div>
                            <div className="fb-item__text">{review.text || <span className="fb-muted">Только оценка</span>}</div>
                        </div>
                    ))}
                </div>
            </div>
            <Calculator accountId={accountId} rating={product.average_rating} count={product.reviews} />
        </div>
    );
}

const COLUMNS = [
    ['name', 'Товар', false], ['average', 'Рейтинг', true], ['reviews', 'Отзывов', true], ['with_text', 'С текстом', true],
    ['positive', 'Позитив', true], ['negative', 'Негатив', true], ['last', 'Последний', true],
];

export default function FeedbackAnalytics({ accountId }) {
    const { request, fetchBlob } = useApi();
    const [section, setSection] = useState('products');
    const [exportError, setExportError] = useState('');
    const exportBase = async (format) => {
        setExportError('');
        try {
            const blob = await downloadFeedbackBase({ fetchBlob, accountId, format });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `feedback_base.${format}`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setExportError(errorText(err));
        }
    };
    const [preset, setPreset] = useState('365');
    const [range, setRange] = useState({ from: '', to: '' });
    const period = useMemo(() => (preset === 'custom' ? { from: range.from, to: range.to } : { days: Number(preset) }), [preset, range]);
    const periodReady = preset !== 'custom' || Boolean(range.from && range.to);
    const [onlyReady, setOnlyReady] = useState(true);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('reviews');
    const [order, setOrder] = useState('desc');
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState({ total: 0, ready_total: 0, items: [] });
    const [selected, setSelected] = useState(null);
    const [picked, setPicked] = useState(() => new Set());
    const [allAssortment, setAllAssortment] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        if (!periodReady) return;
        setLoading(true);
        setError('');
        try {
            setData(await loadAnalyticsProducts({
                request, accountId, period,
                filters: { ready: onlyReady ? true : undefined, q: query.trim(), sort, order, limit: PAGE, offset },
            }));
        } catch (err) {
            setError(errorText(err));
        } finally {
            setLoading(false);
        }
    }, [request, accountId, period, periodReady, onlyReady, query, sort, order, offset]);

    useEffect(() => { reload(); }, [reload]);

    const reset = (setter) => (value) => { setter(value); setOffset(0); };
    const sortBy = (key) => {
        if (key === sort) setOrder(order === 'desc' ? 'asc' : 'desc');
        else { setSort(key); setOrder(key === 'name' || key === 'average' ? 'asc' : 'desc'); }
        setOffset(0);
    };
    const arrow = (key) => (key === sort ? (order === 'desc' ? ' ▼' : ' ▲') : '');
    const items = data.items;
    const pickedSkus = useMemo(() => (allAssortment ? null : [...picked]), [allAssortment, picked]);
    const scopeLabel = allAssortment ? 'весь ассортимент' : picked.size ? `${picked.size} артикулов` : 'ничего: отметьте артикулы галочками или «весь ассортимент» в заголовке';
    const togglePick = (sku) => setPicked((previous) => {
        const next = new Set(previous);
        if (next.has(sku)) next.delete(sku); else next.add(sku);
        return next;
    });
    const pageAllPicked = items.length > 0 && items.every((row) => picked.has(row.sku));
    const togglePage = () => setPicked((previous) => {
        const next = new Set(previous);
        items.forEach((row) => { if (pageAllPicked) next.delete(row.sku); else next.add(row.sku); });
        return next;
    });

    return (
        <div className="fb">
            <div className="account-tabs account-tabs--secondary">
                <button type="button" className={section === 'products' ? 'active' : ''} onClick={() => setSection('products')}>Отзывы</button>
                <button type="button" className={section === 'faq' ? 'active' : ''} onClick={() => setSection('faq')}>Вопросы и чаты</button>
            </div>
            {section === 'faq' && <FeedbackFaq accountId={accountId} />}
            {section === 'products' && <>
            <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>Анализ отзывов по артикулам</h4>
                        <div className="fb-muted">
                            Только отзывы (не вопросы и не чаты) из собранных за период. «Готов для анализа»: у артикула есть 3 и больше отзывов с текстом, тогда ИИ может разобрать, что нравится и что нет.
                        </div>
                    </div>
                    <div className="fb-row" style={{ alignItems: 'center' }}>
                        <select className="fb-input" value={preset} onChange={(event) => reset(setPreset)(event.target.value)}>
                            {PERIODS.map(([value, label]) => <option key={value} value={String(value)}>{label}</option>)}
                            <option value="custom">Свой период</option>
                        </select>
                        {preset === 'custom' && (
                            <>
                                <input className="fb-input" type="date" value={range.from} onChange={(event) => setRange({ ...range, from: event.target.value })} />
                                <input className="fb-input" type="date" value={range.to} onChange={(event) => setRange({ ...range, to: event.target.value })} />
                            </>
                        )}
                    </div>
                </div>
                <div className="fb-row" style={{ alignItems: 'center' }}>
                    <button type="button" title="Все отзывы и вопросы кабинета за всё время со всеми данными и ответами, без имён покупателей: для других ИИ и RAG" onClick={() => exportBase('jsonl')}>Скачать базу (JSONL)</button>
                    <button type="button" onClick={() => exportBase('csv')}>CSV</button>
                    {exportError && <span className="fb-note fb-chip--bad">{exportError}</span>}
                    <label className="fb-note"><input type="checkbox" checked={onlyReady} onChange={(event) => reset(setOnlyReady)(event.target.checked)} /> Только готовые для анализа ({data.ready_total})</label>
                    <input className="fb-input" style={{ width: 260 }} placeholder="Название, артикул или SKU" value={query} onChange={(event) => reset(setQuery)(event.target.value)} />
                    <span className="fb-note">Артикулов: {data.total}{onlyReady ? '' : `, готовых к анализу: ${data.ready_total}`}</span>
                </div>
            </div>
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            <FeedbackReports accountId={accountId} skus={pickedSkus} period={period} scopeLabel={scopeLabel} periodReady={periodReady} hasScope={allAssortment || picked.size > 0} />
            <div className={selected ? 'fb-two' : ''}>
                <div className="fb-table-wrap">
                    <table className="fb-table">
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>
                                    <label className="fb-note" title="Весь ассортимент в отчёт: независимо от фильтров и страниц" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                        <input type="checkbox" checked={allAssortment} onChange={(event) => { setAllAssortment(event.target.checked); if (event.target.checked) setPicked(new Set()); }} />
                                        все
                                    </label>
                                    {!allAssortment && (
                                        <label className="fb-note" title="Отметить все артикулы на этой странице" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <input type="checkbox" checked={pageAllPicked} onChange={togglePage} />
                                            стр.
                                        </label>
                                    )}
                                </th>
                                {COLUMNS.map(([key, label, numeric]) => (
                                    <th key={key} className={numeric ? 'fb-num' : ''} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => sortBy(key)}>{label}{arrow(key)}</th>
                                ))}
                                <th>Оценки</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <tr key={row.sku} className="fb-clickable" onClick={() => setSelected(row)}>
                                    <td onClick={(event) => event.stopPropagation()}>
                                        <input type="checkbox" aria-label="В отчёт" checked={allAssortment || picked.has(row.sku)} disabled={allAssortment} onChange={() => togglePick(row.sku)} />
                                    </td>
                                    <td style={{ maxWidth: 320 }}>
                                        <div>{row.name || '—'} {row.ready && <span className="fb-chip fb-chip--good">готов</span>}</div>
                                        <div className="fb-item__meta">SKU {row.sku}{row.article ? ` · ${row.article}` : ''}</div>
                                    </td>
                                    <td className="fb-num">{row.average_rating ?? '—'}</td>
                                    <td className="fb-num">{row.reviews}</td>
                                    <td className="fb-num">{row.with_text}</td>
                                    <td className="fb-num">{row.positive}</td>
                                    <td className="fb-num">{row.negative ? <span className="fb-chip fb-chip--bad">{row.negative}</span> : 0}</td>
                                    <td className="fb-num">{row.last_review_at ? fmtDateTime(row.last_review_at) : '—'}</td>
                                    <td><Distribution distribution={row.distribution} total={row.reviews} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && items.length === 0 && (
                        <div className="fb-empty">
                            {onlyReady && data.ready_total === 0
                                ? 'За период нет артикулов с 3 и больше текстовыми отзывами. Снимите галочку «Только готовые» или выберите период «Всё время».'
                                : 'За выбранный период отзывов нет. Проверьте, что сбор включён и первая загрузка завершилась.'}
                        </div>
                    )}
                </div>
                {selected && <ProductDetail accountId={accountId} product={selected} period={period} onClose={() => setSelected(null)} />}
            </div>
            {data.total > PAGE && (
                <div className="fb-row">
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - PAGE, 0))}>Назад</button>
                    <span className="fb-note">{offset + 1}–{Math.min(offset + PAGE, data.total)} из {data.total}</span>
                    <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Дальше</button>
                </div>
            )}
            {!selected && <Calculator accountId={accountId} />}
            </>}
        </div>
    );
}
